import React, { useEffect, useRef } from 'react';
import throttle from 'lodash.throttle';
import { gsap } from 'gsap';
import i1 from 'assets/images/portfolio/item1.jpeg';
import i2 from 'assets/images/portfolio/item2.jpeg';
import i3 from 'assets/images/portfolio/item3.jpeg';
import i4 from 'assets/images/portfolio/item4.jpeg';
import i5 from 'assets/images/portfolio/item5.jpeg';
import i6 from 'assets/images/portfolio/item6.jpeg';
import i7 from 'assets/images/portfolio/item7.jpeg';
import i8 from 'assets/images/portfolio/item8.jpeg';
import translator from 'utils/translator';
import styles from './styles.module.scss';

const _t = (path: string) => translator.text(`homepage.${path}`);

const itemsData = [
  {
    src: i1,
    link: 'https://stories.sc/templates/mcdonalds/',
    title: "McDonald's interactive experience",
    format: 'wide',
  },
  {
    src: i2,
    link: 'https://stories.sc/storycards/FSna61ebe/',
    title: 'National Geographic lead experience',
    format: 'tall',
  },
  {
    src: i3,
    link: 'https://stories.sc/templates/lacoste/',
    title: 'Lacoste interactive experience',
    format: 'tall',
  },
  {
    src: i4,
    link: 'https://inspiration.storycards.com/Starbucks/',
    title: 'Starbucks product quiz',
    format: 'wide',
  },
  {
    src: i5,
    link: 'https://stories.sc/templates/samsung/',
    title: 'Samsung interactive experience',
    format: 'wide',
  },
  {
    src: i6,
    link: 'https://stories.sc/templates/Bigi/',
    title: 'Bigi interactive experience',
    format: 'tall',
  },
  {
    src: i7,
    link: 'https://stories.sc/storycards/V9Hdk3pk0/',
    title: 'Interactive data story',
    format: 'wide',
  },
  {
    src: i8,
    link: 'https://stories.sc/templates/tiktok-for-startups/',
    title: 'TikTok for Startups',
    format: 'wide',
  },
];

const Portfolio = () => {
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const gridNode = gridRef.current;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!gridNode || isMobile || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const onMouseMove = throttle(({ pageX }: MouseEvent) => {
      const pageCenter = window.innerWidth / 2;
      const relativePosition = (pageCenter - pageX) / pageCenter;
      gsap.to(gridNode, { xPercent: relativePosition * 2.25, duration: 0.55, ease: 'power1.out' });
    }, 1000 / 30);

    const onMouseLeave = () => {
      onMouseMove.cancel();
      gsap.killTweensOf(gridNode);
      gsap.to(gridNode, { xPercent: 0, duration: 0.6, ease: 'sine.out' });
    };

    gridNode.addEventListener('mousemove', onMouseMove);
    gridNode.addEventListener('mouseleave', onMouseLeave);

    return () => {
      onMouseMove.cancel();
      gsap.killTweensOf(gridNode);
      gridNode.removeEventListener('mousemove', onMouseMove);
      gridNode.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  return (
    <section className={styles.portfolio} aria-labelledby="portfolio-title">
      <div className={styles.portfolioHeader}>
        <h2 id="portfolio-title">{_t('portfolio.title')}</h2>
        <p>{_t('portfolio.subtitle')}</p>
        <a
          href="https://inspire.storycards.com"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.inspireButton}
        >
          <span>Get Inspired</span>
          <svg aria-hidden="true" viewBox="0 0 18 18">
            <path d="M3.75 9h10.5M10 4.75 14.25 9 10 13.25" />
          </svg>
        </a>
      </div>

      <div className={styles.portfolioItems} ref={gridRef}>
        {itemsData.map((item) => (
          <article className={styles.portfolioItem} key={item.link}>
            <div className={item.format === 'wide' ? styles.portfolioItemWide : styles.portfolioItemTall}>
              <img src={item.src} alt="" />
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className={styles.portfolioLink}
                aria-label={`Open ${item.title}`}
              >
                <span>{item.title}</span>
                <svg aria-hidden="true" viewBox="0 0 22 22">
                  <path d="M4.5 17.5 17.5 4.5M8 4.5h9.5V14" />
                </svg>
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default Portfolio;
